import { useMemo, useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Activity,
  ArrowDown,
  ArrowUp,
  Edit,
  FolderTree,
  GripVertical,
  Plus,
  Trash2,
} from 'lucide-react';
import type { CategoryAdminItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { CategoryEditDialog } from '@/components/taxonomies/category-edit-dialog';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import {
  categoriesListQueryOptions,
  useDeleteCategory,
  useReorderCategoriesBulk,
  useReorderCategory,
  useUpdateCategory,
} from '@/lib/taxonomies';

interface CategoriesSearch {
  q?: string;
}

export const Route = createFileRoute('/_authenticated/categories/')({
  validateSearch: (search: Record<string, unknown>): CategoriesSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: CategoriesPage,
});

interface TreeNode {
  category: CategoryAdminItem;
  depth: number;
  /** Direct + descendants. Same as `category.postCount` for leaf
   *  rows, higher for parents that have children. */
  subtreePostCount: number;
}

/**
 * Flatten the category list into a depth-aware ordered array so the table
 * can render parent → children visually via padding. Categories with a
 * dangling parentId (parent deleted or filtered out) fall back to top-level.
 *
 * Also computes `subtreePostCount` per node — the sum of direct + every
 * descendant's postCount. Single bottom-up DFS so the parent's total
 * is ready by the time we push it. O(N) over the input list.
 */
function flattenTree(items: CategoryAdminItem[]): TreeNode[] {
  const byParent = new Map<string | null, CategoryAdminItem[]>();
  const ids = new Set(items.map((c) => c.id));
  for (const c of items) {
    const key = c.parentId && ids.has(c.parentId) ? c.parentId : null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(c);
  }

  // Memoized bottom-up subtree counts — one DFS per root, each id
  // visited once. Avoids O(N²) of calling subtree() during the
  // preorder render walk.
  const subtreeCount = new Map<string, number>();
  const computeSubtree = (id: string, ownCount: number): number => {
    const cached = subtreeCount.get(id);
    if (cached !== undefined) return cached;
    let total = ownCount;
    for (const child of byParent.get(id) ?? []) {
      total += computeSubtree(child.id, child.postCount);
    }
    subtreeCount.set(id, total);
    return total;
  };
  for (const c of items) computeSubtree(c.id, c.postCount);

  const out: TreeNode[] = [];
  const walk = (parentId: string | null, depth: number) => {
    const children = byParent.get(parentId) ?? [];
    for (const c of children) {
      out.push({ category: c, depth, subtreePostCount: subtreeCount.get(c.id) ?? c.postCount });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

function CategoriesPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const query = useQuery(categoriesListQueryOptions(search.q));
  const deleteMutation = useDeleteCategory();
  const updateMutation = useUpdateCategory();
  const reorderMutation = useReorderCategory();
  const reorderBulkMutation = useReorderCategoriesBulk();
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState('');

  // 5px activation distance so clicking inline-rename / row buttons
  // doesn't accidentally start a drag (matches /tags page).
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Inline rename — single row at a time. Mirrors the /tags page
  // pattern; same no-op-on-blank-or-unchanged guard.
  const commitRename = (cat: CategoryAdminItem) => {
    const next = renameDraft.trim();
    setRenameId(null);
    if (!next || next === cat.name) return;
    updateMutation.mutate({ id: cat.id, input: { name: next } });
  };

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryAdminItem | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<CategoryAdminItem | null>(null);
  const [searchInput, setSearchInput] = useState(search.q ?? '');
  // Bulk-reparent selection — checkbox column + toolbar appear once
  // at least one row is selected. Separate from rename state so the
  // admin can browse + queue + commit without losing inline edits.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [reparentOpen, setReparentOpen] = useState(false);
  const [reparentTargetId, setReparentTargetId] = useState<string>('');
  const [reparentSubmitting, setReparentSubmitting] = useState(false);

  const canManage = hasPermission(user, 'category.manage');
  const canReadAudit = hasPermission(user, 'audit.read');
  const items = query.data ?? [];
  const tree = useMemo(() => flattenTree(items), [items]);

  // Map id → "is this category the first / last child of its parent
  // group?" — used to grey out the ↑/↓ button at the edges. We bucket
  // by parentId since reorder swaps within siblings only; the API
  // returns items already sorted by (sortOrder, name) which IS the
  // rendered order within each parent.
  const siblingPositions = useMemo(() => {
    const byParent = new Map<string | null, string[]>();
    for (const c of items) {
      const key = c.parentId ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(c.id);
      byParent.set(key, arr);
    }
    const positions = new Map<string, { isFirst: boolean; isLast: boolean }>();
    for (const ids of byParent.values()) {
      ids.forEach((id, i) => {
        positions.set(id, { isFirst: i === 0, isLast: i === ids.length - 1 });
      });
    }
    return positions;
  }, [items]);

  // Disable reorder while a search filter is active — dragging within a
  // partial list would renumber only the matched subset, which is rarely
  // what the admin wants and confuses the (sortOrder, name) tie-break
  // for siblings that didn't match the filter. Same pattern as /tags.
  const isFiltered = Boolean(search.q);
  const canReorder = canManage && !isFiltered;

  // Drag end — verify both rows share a parent (refuse cross-parent
  // drops with a toast), compute the new ordered list within that
  // sibling group, and post atomically. Top-level rows use
  // parentId=null.
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromItem = items.find((c) => c.id === active.id);
    const toItem = items.find((c) => c.id === over.id);
    if (!fromItem || !toItem) return;
    if ((fromItem.parentId ?? null) !== (toItem.parentId ?? null)) {
      toast.error('ลากได้แค่ในกลุ่มหมวดแม่เดียวกัน — ใช้แก้ไขเพื่อย้ายหมวดแม่');
      return;
    }
    const parentId = fromItem.parentId ?? null;
    const siblingIds = items.filter((c) => (c.parentId ?? null) === parentId).map((c) => c.id);
    const from = siblingIds.indexOf(String(active.id));
    const to = siblingIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    const orderedIds = arrayMove(siblingIds, from, to);
    reorderBulkMutation.mutate({ parentId, orderedIds });
  };

  const flatIds = useMemo(() => tree.map((n) => n.category.id), [tree]);

  // Forbidden-target set for bulk reparent: every selected id +
  // every descendant of every selected id. Moving a selected
  // category UNDER one of its descendants would create a cycle,
  // and "set parent to self" is the trivial case.
  const forbiddenReparentTargets = useMemo(() => {
    if (selected.size === 0) return new Set<string>();
    const byParent = new Map<string | null, string[]>();
    for (const c of items) {
      const key = c.parentId ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(c.id);
      byParent.set(key, arr);
    }
    const forbidden = new Set<string>(selected);
    const stack: string[] = Array.from(selected);
    while (stack.length > 0) {
      const cur = stack.pop()!;
      for (const child of byParent.get(cur) ?? []) {
        if (forbidden.has(child)) continue;
        forbidden.add(child);
        stack.push(child);
      }
    }
    return forbidden;
  }, [items, selected]);

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const openReparentDialog = () => {
    setReparentTargetId('');
    setReparentOpen(true);
  };

  const handleConfirmReparent = async () => {
    if (selected.size === 0) return;
    // Empty string in the select == "— top-level —"; backend expects null.
    const nextParentId = reparentTargetId === '' ? null : reparentTargetId;
    if (nextParentId !== null && forbiddenReparentTargets.has(nextParentId)) {
      // Defense in depth — the option should already be filtered out.
      toast.error('ปลายทางเป็นตัวเองหรือลูกของหมวดที่เลือก');
      return;
    }
    setReparentSubmitting(true);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((id) =>
        updateMutation.mutateAsync({ id, input: { parentId: nextParentId ?? null } }),
      ),
    );
    setReparentSubmitting(false);
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    if (failed === 0) {
      toast.success(`ย้าย ${ok} หมวดเรียบร้อย`);
    } else {
      toast.error(`สำเร็จ ${ok} ล้มเหลว ${failed} — ลองอีกครั้งหรือดู audit`);
    }
    clearSelection();
    setReparentOpen(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { q: searchInput.trim() || undefined } });
  };

  const openCreate = () => {
    setEditing(undefined);
    setEditOpen(true);
  };
  const openEdit = (c: CategoryAdminItem) => {
    setEditing(c);
    setEditOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => setPendingDelete(null),
      onError: () => setPendingDelete(null),
    });
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              หมวดหมู่
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {items.length > 0 ? `${items.length} หมวด` : 'ยังไม่มีหมวด'}
            </p>
          </div>
          {canManage && (
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              สร้างหมวด
            </Button>
          )}
        </div>

        {/* Server-side filter — `q` is forwarded to the existing
            categoriesListQueryOptions. Tree-rendering still works:
            matched children whose parent doesn't match fall back to
            top-level via flattenTree's dangling-parent guard. */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ค้นหาชื่อหรือ slug..."
            className="max-w-md"
          />
          <Button type="submit" variant="outline">
            ค้นหา
          </Button>
          {search.q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: {} });
              }}
            >
              ล้าง
            </Button>
          )}
        </form>

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <FolderTree className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">
              {search.q ? `ไม่พบหมวดที่ตรงกับ "${search.q}"` : 'ยังไม่มีหมวด'}
            </p>
            {!search.q && canManage && (
              <Button size="sm" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                สร้างหมวดแรก
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
            {isFiltered && canManage && (
              <p className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-300">
                ปิดการเรียงลำดับขณะค้นหา — ล้างคำค้นก่อนถึงจะลาก / กด ↑↓ ได้
              </p>
            )}
            {canManage && selected.size > 0 && (
              <div className="bg-brand-50 dark:bg-brand-900/30 flex flex-wrap items-center gap-3 border-b border-slate-200 p-3 dark:border-slate-800">
                <span className="text-sm font-medium">เลือก {selected.size} หมวด</span>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={openReparentDialog}
                  disabled={reparentSubmitting}
                >
                  <FolderTree className="mr-1.5 h-3.5 w-3.5" />
                  ย้ายไปอยู่ใต้...
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="ml-auto"
                >
                  ล้างเลือก
                </Button>
              </div>
            )}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={flatIds} strategy={verticalListSortingStrategy}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
                    <tr>
                      {canReorder && <th className="w-8 px-2 py-3 font-medium" />}
                      {canManage && <th className="w-10 px-4 py-3 font-medium" />}
                      <th className="px-4 py-3 font-medium">ชื่อ</th>
                      <th className="px-4 py-3 font-medium">Slug</th>
                      <th className="px-4 py-3 font-medium">บทความ</th>
                      <th className="px-4 py-3 font-medium">แก้ไขล่าสุด</th>
                      {(canManage || canReadAudit) && <th className="w-52 px-4 py-3 font-medium" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {tree.map(({ category: c, depth, subtreePostCount }) => (
                      <CategoryRow
                        key={c.id}
                        category={c}
                        depth={depth}
                        subtreePostCount={subtreePostCount}
                        canManage={canManage}
                        canReorder={canReorder}
                        canReadAudit={canReadAudit}
                        selected={selected.has(c.id)}
                        onToggleSelect={() => toggleSelect(c.id)}
                        renameActive={renameId === c.id}
                        renameDraft={renameDraft}
                        onRenameStart={() => {
                          setRenameId(c.id);
                          setRenameDraft(c.name);
                        }}
                        onRenameChange={setRenameDraft}
                        onRenameCommit={() => commitRename(c)}
                        onRenameCancel={() => setRenameId(null)}
                        renameDisabled={updateMutation.isPending}
                        onMoveUp={() => reorderMutation.mutate({ id: c.id, direction: 'up' })}
                        onMoveDown={() => reorderMutation.mutate({ id: c.id, direction: 'down' })}
                        reorderPending={reorderMutation.isPending || reorderBulkMutation.isPending}
                        siblingPosition={
                          siblingPositions.get(c.id) ?? { isFirst: false, isLast: false }
                        }
                        onEdit={() => openEdit(c)}
                        onDelete={() => setPendingDelete(c)}
                        deleteDisabled={deleteMutation.isPending}
                      />
                    ))}
                  </tbody>
                </table>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>

      <CategoryEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={editing}
        allCategories={items}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ยืนยันการลบหมวด"
        description={
          pendingDelete
            ? `จะลบ "${pendingDelete.name}" (มี ${pendingDelete.postCount} บทความผูกอยู่ — จะถูกถอดออกจากหมวดนี้ แต่บทความจะยังอยู่). หมวดลูกจะกลายเป็น top-level.`
            : undefined
        }
        confirmLabel="ลบ"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />

      <Dialog
        open={reparentOpen}
        onOpenChange={(open) => {
          if (!open && !reparentSubmitting) setReparentOpen(false);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5 text-slate-400" />
              ย้าย {selected.size} หมวดไปอยู่ใต้...
            </DialogTitle>
            <DialogDescription>
              เลือกหมวดแม่ใหม่ — ลูกหมวดเก่าจะตามไปด้วยอัตโนมัติ (ตัวเอง +
              ลูกในระดับล่างถูกตัดออกจาก dropdown เพื่อกัน cycle).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs dark:border-slate-700 dark:bg-slate-800/50">
              <div className="mb-1 font-medium text-slate-600 dark:text-slate-300">
                หมวดที่จะย้าย:
              </div>
              <div className="flex flex-wrap gap-1">
                {Array.from(selected).map((id) => {
                  const cat = items.find((c) => c.id === id);
                  return (
                    <span
                      key={id}
                      className="bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 inline-flex items-center rounded-full px-2 py-0.5 text-[11px]"
                    >
                      {cat?.name ?? id.slice(0, 8)}
                    </span>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="reparent-target" className="mb-1.5 block">
                หมวดแม่ใหม่
              </Label>
              <select
                id="reparent-target"
                value={reparentTargetId}
                onChange={(e) => setReparentTargetId(e.target.value)}
                disabled={reparentSubmitting}
                className="border-input bg-background flex h-10 w-full rounded-md border px-3 text-sm"
              >
                <option value="">— ไม่มี (top-level) —</option>
                {items
                  .filter((c) => !forbiddenReparentTargets.has(c.id))
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setReparentOpen(false)}
              disabled={reparentSubmitting}
            >
              ยกเลิก
            </Button>
            <Button
              type="button"
              onClick={() => void handleConfirmReparent()}
              disabled={reparentSubmitting}
            >
              {reparentSubmitting ? 'กำลังย้าย...' : 'ย้าย'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface CategoryRowProps {
  category: CategoryAdminItem;
  depth: number;
  subtreePostCount: number;
  canManage: boolean;
  canReorder: boolean;
  canReadAudit: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  renameActive: boolean;
  renameDraft: string;
  onRenameStart: () => void;
  onRenameChange: (v: string) => void;
  onRenameCommit: () => void;
  onRenameCancel: () => void;
  renameDisabled: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  reorderPending: boolean;
  siblingPosition: { isFirst: boolean; isLast: boolean };
  onEdit: () => void;
  onDelete: () => void;
  deleteDisabled: boolean;
}

function CategoryRow({
  category: c,
  depth,
  subtreePostCount,
  canManage,
  canReorder,
  canReadAudit,
  selected,
  onToggleSelect,
  renameActive,
  renameDraft,
  onRenameStart,
  onRenameChange,
  onRenameCommit,
  onRenameCancel,
  renameDisabled,
  onMoveUp,
  onMoveDown,
  reorderPending,
  siblingPosition,
  onEdit,
  onDelete,
  deleteDisabled,
}: CategoryRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: c.id,
    disabled: !canReorder,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={
        isDragging
          ? 'bg-brand-50/60 dark:bg-brand-900/30 relative z-10 shadow-md'
          : selected
            ? 'bg-brand-50/50 dark:bg-brand-900/20'
            : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
      }
    >
      {canReorder && (
        <td className="px-2 py-3 align-middle">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab text-slate-400 hover:text-slate-600 active:cursor-grabbing dark:hover:text-slate-200"
            aria-label="ลากเรียงลำดับ"
            title="ลากเพื่อเรียงลำดับในกลุ่มหมวดแม่เดียวกัน"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        </td>
      )}
      {canManage && (
        <td className="px-4 py-3">
          <input
            type="checkbox"
            aria-label={`เลือก ${c.name}`}
            checked={selected}
            onChange={onToggleSelect}
            className="h-4 w-4"
          />
        </td>
      )}
      <td className="px-4 py-3">
        <div style={{ paddingLeft: `${depth * 20}px` }}>
          {canManage && renameActive ? (
            <Input
              autoFocus
              value={renameDraft}
              onChange={(e) => onRenameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onRenameCommit();
                else if (e.key === 'Escape') onRenameCancel();
              }}
              onBlur={onRenameCommit}
              disabled={renameDisabled}
              className="h-7 max-w-xs px-2 py-0 text-sm"
            />
          ) : canManage ? (
            <button
              type="button"
              onClick={onRenameStart}
              className="-mx-1 rounded px-1 text-left font-medium text-slate-900 hover:bg-slate-100 dark:text-slate-50 dark:hover:bg-slate-800"
              title="คลิกเพื่อแก้ชื่อ"
            >
              {depth > 0 && <span className="text-slate-400">└─ </span>}
              {c.name}
            </button>
          ) : (
            <span className="font-medium text-slate-900 dark:text-slate-50">
              {depth > 0 && <span className="text-slate-400">└─ </span>}
              {c.name}
            </span>
          )}
          {c.description && (
            <p className="mt-0.5 line-clamp-1 text-[11px] text-slate-500">{c.description}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard
              .writeText(`/${c.slug}`)
              .then(() => toast.success(`คัดลอก /${c.slug}`))
              .catch(() => {
                // Clipboard denied (non-secure context) — silent.
              });
          }}
          className="hover:text-brand-600 dark:hover:text-brand-400 font-mono text-xs text-slate-500"
          title="คลิกเพื่อคัดลอก"
        >
          /{c.slug}
        </button>
      </td>
      <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
        {c.postCount > 0 ? (
          <Link
            to="/posts"
            search={{ categoryId: c.id }}
            className="hover:text-brand-600 dark:hover:text-brand-400 underline-offset-2 hover:underline"
            title={`ดูบทความในหมวด ${c.name}`}
          >
            {c.postCount}
          </Link>
        ) : (
          <span className="text-slate-400">0</span>
        )}
        {subtreePostCount > c.postCount && (
          <span
            className="ml-1 text-[10px] text-slate-400"
            title={`รวมบทความทั้งสาขา (${subtreePostCount - c.postCount} ใต้)`}
          >
            (รวมสาขา {subtreePostCount})
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">
        {new Date(c.updatedAt).toLocaleDateString('th-TH')}
        {Date.now() - new Date(c.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
          <span className="ml-1 inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
            ใหม่
          </span>
        )}
      </td>
      {(canManage || canReadAudit) && (
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            {canManage && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onMoveUp}
                  disabled={reorderPending || siblingPosition.isFirst || !canReorder}
                  aria-label="เลื่อนขึ้น"
                  title="เลื่อนขึ้นในกลุ่มเดียวกัน"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onMoveDown}
                  disabled={reorderPending || siblingPosition.isLast || !canReorder}
                  aria-label="เลื่อนลง"
                  title="เลื่อนลงในกลุ่มเดียวกัน"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </>
            )}
            {canReadAudit && (
              <Link
                to="/audit"
                search={{ resourceType: 'category', resourceId: c.id }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                title="ดู audit ของหมวดนี้"
                aria-label="ดู audit"
              >
                <Activity className="h-4 w-4" />
              </Link>
            )}
            {canManage && (
              <Button variant="ghost" size="icon" onClick={onEdit} aria-label="แก้ไข">
                <Edit className="h-4 w-4" />
              </Button>
            )}
            {canManage && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onDelete}
                disabled={deleteDisabled}
                aria-label="ลบ"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            )}
          </div>
        </td>
      )}
    </tr>
  );
}
